/**
 * EloBadge — tier-colored ELO display chip.
 *
 * Sizes:
 *   'nano'   — top bar / inline (just the number, very compact)
 *   'small'  — lobby rows, match history table cells
 *   'medium' — match player cards, friend rows
 *   'large'  — profile hero (bracket label shown, bigger numeral)
 *
 * The tier label below the number is hidden by default across all sizes.
 * Pass `showTierLabel` explicitly to opt in — the tooltip always shows tier info on hover.
 *
 * The Orbitron font is already loaded globally via renderFullPage.js.
 */
import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { getEloTier, formatElo } from './eloTierUtils';

/** Size-variant config */
const SIZE_CONFIG = {
  nano: {
    px: '0.52rem',
    py: '0.3rem',
    numberSize: '0.82rem',
    labelSize: '0.52rem',
    borderRadius: '5px',
    borderWidth: '1px',
    gap: '0',
    showTierLabelDefault: false,
    letterSpacing: '0.04em',
  },
  small: {
    px: '0.62rem',
    py: '0.3rem',
    numberSize: '0.84rem',
    labelSize: '0.56rem',
    borderRadius: '6px',
    borderWidth: '1px',
    gap: '0.08rem',
    showTierLabelDefault: false,
    letterSpacing: '0.04em',
  },
  medium: {
    px: '0.65rem',
    py: '0.26rem',
    numberSize: '1rem',
    labelSize: '0.6rem',
    borderRadius: '7px',
    borderWidth: '1px',
    gap: '0.1rem',
    showTierLabelDefault: false,
    letterSpacing: '0.05em',
  },
  large: {
    px: '1rem',
    py: '0.45rem',
    numberSize: '1.5rem',
    labelSize: '0.68rem',
    borderRadius: '9px',
    borderWidth: '1.5px',
    gap: '0.18rem',
    showTierLabelDefault: false,
    letterSpacing: '0.06em',
  },
};

/**
 * Inner badge box — extracted so Tooltip can wrap it.
 * forwardRef is needed for MUI Tooltip to attach its ref.
 */
const ELoBadgeInner = React.forwardRef(function EloBadgeInner(
  { tier, displayValue, sizeConf, showTierLabel, style, className, ...other },
  ref
) {
  return (
    <Box
      ref={ref}
      className={className}
      style={style}
      {...other}
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sizeConf.gap,
        px: sizeConf.px,
        py: sizeConf.py,
        borderRadius: sizeConf.borderRadius,
        backgroundColor: tier.bg,
        border: `${sizeConf.borderWidth} solid ${tier.border}`,
        boxSizing: 'border-box',
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      <Typography
        component="span"
        sx={{
          fontFamily: "'Orbitron', sans-serif",
          fontSize: sizeConf.numberSize,
          fontWeight: 700,
          color: tier.text,
          lineHeight: 1,
          letterSpacing: sizeConf.letterSpacing,
          whiteSpace: 'nowrap',
        }}
      >
        {displayValue}
      </Typography>
      {showTierLabel && (
        <Typography
          component="span"
          sx={{
            fontFamily: "'Orbitron', sans-serif",
            fontSize: sizeConf.labelSize,
            fontWeight: 600,
            color: tier.text,
            opacity: 0.78,
            lineHeight: 1,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          {tier.label}
        </Typography>
      )}
    </Box>
  );
});

ELoBadgeInner.displayName = 'EloBadgeInner';

/**
 * Tooltip content shown on hover — displays full tier info and ELO range.
 */
function EloTooltipContent({ tier, value }) {
  const rangeText =
    tier.max === Infinity
      ? `${tier.min.toLocaleString()}+`
      : `${tier.min.toLocaleString()} – ${tier.max.toLocaleString()}`;

  return (
    <Box sx={{ p: '0.25rem 0.1rem', minWidth: 110 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: tier.text, mb: '2px' }}>
        {tier.label}
      </Typography>
      <Typography sx={{ fontSize: '0.68rem', color: 'rgba(200,210,220,0.8)' }}>
        Range: {rangeText}
      </Typography>
      {value != null && Number.isFinite(Number(value)) && (
        <Typography sx={{ fontSize: '0.68rem', color: 'rgba(200,210,220,0.7)', mt: '1px' }}>
          ELO: {Math.round(Number(value)).toLocaleString()}
        </Typography>
      )}
    </Box>
  );
}

export default function EloBadge({
  value,
  size = 'medium',
  showTierLabel,
  tooltip = true,
  style,
  className,
}) {
  const tier = getEloTier(value);
  const displayValue = formatElo(value);
  const sizeConf = SIZE_CONFIG[size] || SIZE_CONFIG.medium;
  const resolvedShowTierLabel =
    showTierLabel !== undefined ? showTierLabel : sizeConf.showTierLabelDefault;

  const badge = (
    <ELoBadgeInner
      tier={tier}
      displayValue={displayValue}
      sizeConf={sizeConf}
      showTierLabel={resolvedShowTierLabel}
      style={style}
      className={className}
    />
  );

  if (!tooltip) return badge;

  return (
    <Tooltip
      title={<EloTooltipContent tier={tier} value={value} />}
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#1C1E20',
            border: `1px solid ${tier.border}`,
            borderRadius: '7px',
            px: '0.6rem',
            py: '0.4rem',
          },
        },
      }}
    >
      {badge}
    </Tooltip>
  );
}

EloBadge.propTypes = {
  /** Raw ELO number (or string coercible to a number). */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Visual size variant. */
  size: PropTypes.oneOf(['nano', 'small', 'medium', 'large']),
  /** Override whether the tier label text is shown below the number. */
  showTierLabel: PropTypes.bool,
  /** Whether to wrap with an info tooltip on hover. */
  tooltip: PropTypes.bool,
  /** Optional inline style for the outer Box. */
  style: PropTypes.object,
  className: PropTypes.string,
};
